import { useState } from "react";
import { Camera, ChevronLeft, ChevronRight, Download, MapPin, X } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import { BotonDescartarDeTerreno } from "./DescarteDeTerreno";
import { urlDelArchivoDeTerreno, type FotoDeTerreno } from "@/lib/terreno";

import {
  esFormatoSinVistaPrevia,
  horaDeCaptura,
  momentoDeSincronizacion,
  ubicacionDeLaFoto,
  type FotoDelVisor,
} from "./registroDeTerreno";

interface ImagenProps {
  informeId: number;
  uuid: string;
  hash: string;
  mime: string;
  alt: string;
  className?: string;
}

/**
 * La imagen de un registro, o el recuadro que dice por qué no hay imagen.
 *
 * DOS CASOS DISTINTOS y los dos se dicen: un HEIC —el formato de la cámara del
 * iPhone— no lo dibuja ningún navegador salvo Safari, así que se avisa el
 * formato y se ofrece la descarga; y un archivo que no cargó (borrado, sin
 * red) no deja un ícono roto sino un mensaje.
 */
export function ImagenDeTerreno({ informeId, uuid, hash, mime, alt, className }: ImagenProps) {
  const [fallo, setFallo] = useState(false);

  if (esFormatoSinVistaPrevia(mime) || fallo) {
    return (
      <div
        className={cn(
          "bg-muted text-ink-muted flex size-full flex-col items-center justify-center gap-1.5",
          className,
        )}
      >
        <Camera className="size-5" aria-hidden="true" />
        <span className="px-2 text-center text-[0.6875rem] font-bold">
          {fallo && !esFormatoSinVistaPrevia(mime)
            ? "No se pudo cargar la imagen"
            : `Formato ${mime.split("/")[1]?.toUpperCase() ?? "desconocido"}`}
        </span>
      </div>
    );
  }

  return (
    <img
      src={urlDelArchivoDeTerreno(informeId, uuid, hash)}
      alt={alt}
      loading="lazy"
      onError={() => setFallo(true)}
      className={cn("size-full object-cover", className)}
    />
  );
}

interface UbicacionProps {
  latitud: number | null;
  longitud: number | null;
  className?: string;
}

/**
 * Dónde se tomó una fotografía: las coordenadas con su enlace al mapa, o
 * "Sin ubicación". Una foto de galería, o tomada sin permiso de ubicación,
 * llega sin coordenadas y se dice —callarlo dejaría pensar que el dato se
 * perdió—; nunca se muestra una posición que la app no registró.
 */
export function UbicacionDeLaFotoDeTerreno({ latitud, longitud, className }: UbicacionProps) {
  const ubicacion = ubicacionDeLaFoto({ latitud, longitud });

  if (!ubicacion) {
    return (
      <p className={cn("text-ink-hint flex items-center gap-1 text-[0.6875rem] font-semibold", className)}>
        <MapPin className="size-3 flex-none" aria-hidden="true" />
        Sin ubicación
      </p>
    );
  }

  return (
    <p
      className={cn(
        "text-ink-muted flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[0.6875rem] font-semibold",
        className,
      )}
    >
      <MapPin className="text-success-soft-foreground size-3 flex-none" aria-hidden="true" />
      <span className="font-mono">{ubicacion.texto}</span>
      <a
        href={ubicacion.urlDelMapa}
        target="_blank"
        rel="noopener noreferrer"
        className="text-success-soft-foreground hover:text-ink font-bold"
      >
        Ver en el mapa
      </a>
    </p>
  );
}

function EnlaceDeDescarga({
  informeId,
  uuid,
  hash,
  contexto,
}: {
  informeId: number;
  uuid: string;
  hash: string;
  contexto: string;
}) {
  return (
    <a
      href={urlDelArchivoDeTerreno(informeId, uuid, hash)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Descargar fotografía · ${contexto}`}
      className="text-success-soft-foreground hover:text-ink inline-flex w-fit items-center gap-1 text-xs font-bold"
    >
      <Download className="size-3.5" aria-hidden="true" />
      Descargar
    </a>
  );
}

interface TarjetaDeFotoProps {
  informeId: number;
  foto: FotoDeTerreno;
  onAbrir: () => void;
  /** Dónde se tomó: sección · ítem · registro. Nombra la foto para quien no la ve. */
  contexto: string;
  /** La portada es ancha. */
  esPortada?: boolean;
  /**
   * Sin botón «Descartar» propio. Lo apaga la foto PROPIA de un registro: su
   * uuid es el del registro, así que descartarla descartaría el registro
   * entero (texto, audios y las demás fotos) bajo una etiqueta que dice
   * «fotografía»; para eso está el botón del registro.
   */
  sinDescartar?: boolean;
}

/**
 * La miniatura de una foto: abre el visor al hacer clic. Con HEIC, además,
 * un enlace de descarga APARTE de la miniatura (un enlace dentro de un botón
 * es HTML inválido y se comporta distinto en cada navegador).
 */
export function TarjetaDeFoto({ informeId, foto, contexto, onAbrir, esPortada = false, sinDescartar = false }: TarjetaDeFotoProps) {
  if (!foto.archivo) return null;

  const sinVistaPrevia = esFormatoSinVistaPrevia(foto.archivo.mime);

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <button
        type="button"
        onClick={onAbrir}
        aria-label={`Ampliar fotografía · ${contexto}`}
        title="Ampliar"
        className={cn(
          "bg-muted focus-visible:ring-ring/50 hover:ring-primary relative overflow-hidden rounded-lg outline-none hover:ring-2 focus-visible:ring-[3px]",
          esPortada ? "aspect-[16/7] max-h-[16rem]" : "aspect-[4/3]",
        )}
      >
        <ImagenDeTerreno
          informeId={informeId}
          uuid={foto.uuid}
          hash={foto.archivo.hash}
          mime={foto.archivo.mime}
          alt={`Fotografía · ${contexto}`}
        />
        <span className="bg-card/90 text-ink-body absolute bottom-1.5 left-1.5 rounded-md px-1.5 py-0.5 font-mono text-[0.625rem] font-bold">
          {horaDeCaptura(foto.capturado_en)}
        </span>
      </button>

      <div className="flex items-center justify-between gap-2">
        <p className="text-ink-muted font-mono text-[0.625rem] font-semibold">
          Sinc. {momentoDeSincronizacion(foto.sincronizado_en)}
        </p>
        {!sinDescartar && (
          <BotonDescartarDeTerreno uuid={foto.uuid} etiqueta={`fotografía · ${contexto}`} />
        )}
      </div>
      <UbicacionDeLaFotoDeTerreno latitud={foto.latitud} longitud={foto.longitud} />
      {sinVistaPrevia && (
        <EnlaceDeDescarga
          informeId={informeId}
          uuid={foto.uuid}
          hash={foto.archivo.hash}
          contexto={contexto}
        />
      )}
    </div>
  );
}

interface VisorDeFotosProps {
  informeId: number;
  /** TODAS las fotos que la pantalla muestra ahora, en orden de dibujo. */
  fotos: FotoDelVisor[];
  /** El uuid de la foto abierta, o `null` con el visor cerrado. */
  abierta: string | null;
  onCambiar: (uuid: string | null) => void;
}

/**
 * El visor de fotografías: una `Dialog` del DS con anterior/siguiente, que
 * recorre el MISMO conjunto que muestra la pantalla ("N de M").
 *
 * Los botones de navegación van DENTRO de la caja y no fijos a la ventana como
 * en el diseño: el contenido de un `Dialog` se centra con una transformación,
 * y todo `position: fixed` dentro de un ancestro transformado se ancla a él y
 * no a la ventana.
 *
 * Se guarda el UUID y no el índice: si la consulta se refresca con el visor
 * abierto y la lista se mueve, la foto que se está mirando sigue siendo esa.
 */
export function VisorDeFotos({ informeId, fotos, abierta, onCambiar }: VisorDeFotosProps) {
  const indice = abierta === null ? -1 : fotos.findIndex((f) => f.uuid === abierta);
  const foto = indice >= 0 ? fotos[indice] : null;

  function mover(paso: number) {
    if (fotos.length === 0 || indice < 0) return;

    onCambiar(fotos[(indice + paso + fotos.length) % fotos.length]!.uuid);
  }

  return (
    <Dialog open={foto !== null} onOpenChange={(abierto) => !abierto && onCambiar(null)}>
      {foto && (
        <DialogContent
          showCloseButton={false}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") {
              e.preventDefault();
              mover(1);
            } else if (e.key === "ArrowLeft") {
              e.preventDefault();
              mover(-1);
            }
          }}
          className="gap-0 overflow-hidden p-0 sm:max-w-[61rem]"
        >
          <div className="bg-superficie-fija relative flex h-[min(68vh,35rem)] items-center justify-center">
            <ImagenDeTerreno
              key={foto.uuid}
              informeId={informeId}
              uuid={foto.uuid}
              hash={foto.archivo.hash}
              mime={foto.archivo.mime}
              alt={`Fotografía · ${foto.contexto}`}
              className="size-auto max-h-full max-w-full object-contain"
            />

            {esFormatoSinVistaPrevia(foto.archivo.mime) && (
              <div className="bg-superficie-fija-foreground/90 absolute bottom-3 left-3 rounded-md px-2 py-1">
                <EnlaceDeDescarga
                  informeId={informeId}
                  uuid={foto.uuid}
                  hash={foto.archivo.hash}
                  contexto={foto.contexto}
                />
              </div>
            )}

            <BotonDelVisor etiqueta="Foto anterior" lado="left-3" onClick={() => mover(-1)}>
              <ChevronLeft className="size-5" />
            </BotonDelVisor>
            <BotonDelVisor etiqueta="Foto siguiente" lado="right-3" onClick={() => mover(1)}>
              <ChevronRight className="size-5" />
            </BotonDelVisor>
            <DialogClose
              aria-label="Cerrar"
              className="bg-superficie-fija-foreground/15 text-superficie-fija-foreground hover:bg-superficie-fija-foreground/25 focus-visible:ring-ring/50 absolute top-3 right-3 flex size-10 items-center justify-center rounded-full outline-none focus-visible:ring-[3px]"
            >
              <X className="size-[1.125rem]" />
            </DialogClose>
          </div>

          <div className="flex items-center gap-3.5 px-4 py-3">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-ink text-sm leading-snug font-bold text-pretty">
                {foto.contexto}
              </DialogTitle>
              <DialogDescription className="sr-only">Fotografía del registro de terreno</DialogDescription>
              <UbicacionDeLaFotoDeTerreno latitud={foto.latitud} longitud={foto.longitud} className="mt-1" />
            </div>
            <span className="text-ink-muted flex-none font-mono text-xs font-bold">
              {horaDeCaptura(foto.capturado_en)}
            </span>
            <span className="bg-muted text-ink flex-none rounded-md px-2 py-1 font-mono text-xs font-bold">
              {indice + 1} de {fotos.length}
            </span>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}

function BotonDelVisor({
  etiqueta,
  lado,
  onClick,
  children,
}: {
  etiqueta: string;
  lado: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={etiqueta}
      title={etiqueta}
      className={cn(
        "bg-superficie-fija-foreground/15 text-superficie-fija-foreground hover:bg-superficie-fija-foreground/25 focus-visible:ring-ring/50 absolute top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full outline-none focus-visible:ring-[3px]",
        lado,
      )}
    >
      {children}
    </button>
  );
}
